import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { Triangle } from "react-loader-spinner";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import AdvertisersService from "../../../services/OrderingServices/AdvertisersService";

const AdvertiserForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const validationSchema = Yup.object().shape({
    advertiserName: Yup.string().required("Advertiser name is required"),
    advertiserLocation: Yup.string(),
    advertiserContact: Yup.string(),
    advertiserEmail: Yup.string()
      .email("Invalid email format")
      .required("Advertiser email is required"),
    contactPerson: Yup.string(),
    contactPhone: Yup.string(),
    contactEmail: Yup.string().email("Invalid email format"),
  });

  const formik = useFormik({
    initialValues: {
      advertiserName: "",
      advertiserLocation: "",
      advertiserContact: "",
      advertiserEmail: "",
      contactPerson: "",
      contactPhone: "",
      contactEmail: "",
      advertiserPosition: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        if (id && id !== "new") {
          await AdvertisersService.update(id, values);
          toast.success("Advertiser updated successfully");
        } else {
          await AdvertisersService.create(values);
          toast.success("Advertiser created successfully");
        }
        navigate("/advertiser-list");
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            (id
              ? "Failed to update advertiser"
              : "Failed to create advertiser"),
        );
        console.error(error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (id && id !== "new") {
      fetchAdvertiserDetail();
    }
  }, [id]);

  async function fetchAdvertiserDetail() {
    try {
      setLoading(true);
      const response = await AdvertisersService.getAdvertiserById(id);
      if (response) {
        const advertiserData = response.data || response;
        formik.setValues({
          advertiserName: advertiserData.advertiserName || "",
          advertiserLocation: advertiserData.advertiserLocation || "",
          advertiserContact: advertiserData.advertiserContact || "",
          advertiserEmail: advertiserData.advertiserEmail || "",
          contactPerson: advertiserData.contactPerson || "",
          contactPhone: advertiserData.contactPhone || "",
          contactEmail: advertiserData.contactEmail || "",
        });
      }
    } catch (error) {
      toast.error("Failed to fetch advertiser details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const handleCancel = () => {
    navigate("/advertiser-list");
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Triangle color="#435ebe" height={80} width={80} />
      </div>
    );
  }

  const breadcrumbItems = [
    { title: "Back To List", onClick: () => navigate(-1) },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <BreadCrumb
            title={id && id !== "new" ? "Edit Advertiser" : "New Advertiser"}
            pageTitle="Advertisers"
            breadcrumbItems={breadcrumbItems}
          />
        </div>
        <Container fluid>
          <Row>
            <Col lg="8" className="mx-auto">
              <Card className="border-0 shadow-sm">
                <CardHeader className="bg-light">
                  <h5 className="mb-0">
                    {id && id !== "new"
                      ? "Edit Advertiser Information"
                      : "Add New Advertiser"}
                  </h5>
                </CardHeader>
                <CardBody className="p-4">
                  <Form onSubmit={formik.handleSubmit}>
                    <Row>
                      {/* Column 1 */}
                      <Col md="6">
                        {/* Advertiser Name */}
                        <FormGroup className="mb-3">
                          <Label htmlFor="advertiserName" className="fw-bold">
                            Advertiser Name
                            <span style={{ color: "red" }}>*</span>
                          </Label>
                          <Input
                            type="text"
                            name="advertiserName"
                            id="advertiserName"
                            placeholder="Enter advertiser name"
                            value={formik.values.advertiserName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              !!(
                                formik.touched.advertiserName &&
                                formik.errors.advertiserName
                              )
                            }
                          />
                          {formik.touched.advertiserName &&
                            formik.errors.advertiserName && (
                              <FormFeedback>
                                {formik.errors.advertiserName}
                              </FormFeedback>
                            )}
                        </FormGroup>

                        {/* Advertiser Email */}
                        <FormGroup className="mb-3">
                          <Label htmlFor="advertiserEmail" className="fw-bold">
                            Email
                            <span style={{ color: "red" }}>*</span>
                          </Label>
                          <Input
                            type="email"
                            name="advertiserEmail"
                            id="advertiserEmail"
                            placeholder="Enter advertiser email"
                            value={formik.values.advertiserEmail}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              !!(
                                formik.touched.advertiserEmail &&
                                formik.errors.advertiserEmail
                              )
                            }
                          />
                          {formik.touched.advertiserEmail &&
                            formik.errors.advertiserEmail && (
                              <FormFeedback>
                                {formik.errors.advertiserEmail}
                              </FormFeedback>
                            )}
                        </FormGroup>

                        {/* Advertiser Position */}
                        <FormGroup className="mb-3">
                          <Label
                            htmlFor="advertiserPosition"
                            className="fw-bold"
                          >
                            Position
                            {/* <span style={{ color: "red" }}>*</span> */}
                          </Label>
                          <Input
                            type="text"
                            name="advertiserPosition"
                            id="advertiserPosition"
                            placeholder="Enter advertiser position"
                            value={formik.values.advertiserPosition}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              !!(
                                formik.touched.advertiserPosition &&
                                formik.errors.advertiserPosition
                              )
                            }
                          />
                          {formik.touched.advertiserPosition &&
                            formik.errors.advertiserPosition && (
                              <FormFeedback>
                                {formik.errors.advertiserPosition}
                              </FormFeedback>
                            )}
                        </FormGroup>

                        {/* Advertiser Contact */}
                        <FormGroup className="mb-3">
                          <Label
                            htmlFor="advertiserContact"
                            className="fw-bold"
                          >
                            Contact
                          </Label>
                          <Input
                            type="text"
                            name="advertiserContact"
                            id="advertiserContact"
                            placeholder="Enter advertiser contact"
                            value={formik.values.advertiserContact}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </FormGroup>

                        {/* Advertiser Location */}
                        <FormGroup className="mb-3">
                          <Label
                            htmlFor="advertiserLocation"
                            className="fw-bold"
                          >
                            Location
                          </Label>
                          <Input
                            type="text"
                            name="advertiserLocation"
                            id="advertiserLocation"
                            placeholder="Enter advertiser location"
                            value={formik.values.advertiserLocation}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </FormGroup>
                      </Col>

                      {/* Column 2 */}
                      <Col md="6">
                        {/* Contact Person */}
                        <FormGroup className="mb-3">
                          <Label htmlFor="contactPerson" className="fw-bold">
                            Contact Person
                          </Label>
                          <Input
                            type="text"
                            name="contactPerson"
                            id="contactPerson"
                            placeholder="Enter contact person name"
                            value={formik.values.contactPerson}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </FormGroup>

                        {/* Contact Email */}
                        <FormGroup className="mb-3">
                          <Label htmlFor="contactEmail" className="fw-bold">
                            Contact Email
                          </Label>
                          <Input
                            type="email"
                            name="contactEmail"
                            id="contactEmail"
                            placeholder="Enter contact email"
                            value={formik.values.contactEmail}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              !!(
                                formik.touched.contactEmail &&
                                formik.errors.contactEmail
                              )
                            }
                          />
                          {formik.touched.contactEmail &&
                            formik.errors.contactEmail && (
                              <FormFeedback>
                                {formik.errors.contactEmail}
                              </FormFeedback>
                            )}
                        </FormGroup>

                        {/* Contact Phone */}
                        <FormGroup className="mb-3">
                          <Label htmlFor="contactPhone" className="fw-bold">
                            Contact Phone
                          </Label>
                          <Input
                            type="tel"
                            name="contactPhone"
                            id="contactPhone"
                            placeholder="Enter contact phone"
                            value={formik.values.contactPhone}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Action Buttons */}
                    <Row className="mt-4">
                      <Col className="d-flex gap-2 justify-content-end">
                        <Button
                          color="secondary"
                          outline
                          type="button"
                          onClick={handleCancel}
                          disabled={submitting}
                        >
                          Cancel
                        </Button>
                        <Button
                          color="primary"
                          type="submit"
                          disabled={submitting}
                        >
                          {submitting ? "Saving..." : "Save"}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AdvertiserForm;
